import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { bulkAddTourEvent } from './actions/bulk-add-tour-event.action';
import { getTourEvents } from './actions/get-tour-events.action';
import { getTourSequences } from './actions/get-tour-sequences.action';
import { getTourTravels } from './actions/get-tour-travels.action';
import { importTourEventsFromCsv } from './actions/import-tour-events-from-csv';
import { importTourEventsFromOrfeo } from './actions/import-tour-events-from-orfeo';
import { prepareAndAddTourTravel } from './actions/prepare-and-add-tour-travel.action';
import { TourEvent } from './entities/project-tour-ontour-event.entity';
import { TourSequence } from './entities/project-tour-ontour-sequence.entity';
import { TourTravel } from './entities/project-tour-ontour-travel.entity';

export type ProjectTourOntourState = {
  tourSequences: TourSequence[];
  tourEvents: TourEvent[];
  tourTravels: TourTravel[];
  currentSequenceId: number | null;
  isFetchingTourTravels: boolean;
  isFetchingTourEvents: boolean;
  isCreatingTourTravel: boolean;
  isCreatingTourEvent: boolean;
  isImportingTourEvents: boolean;
  isImportingTourEventsFromCsv: boolean;
};

const initialState: ProjectTourOntourState = {
  tourSequences: [],
  tourEvents: [],
  tourTravels: [],
  currentSequenceId: null,
  isFetchingTourTravels: false,
  isFetchingTourEvents: false,
  isCreatingTourTravel: false,
  isCreatingTourEvent: false,
  isImportingTourEvents: false,
  isImportingTourEventsFromCsv: false,
};

export const projectTourOntourSlice = createSlice({
  name: 'projectTourOntour',
  initialState,
  reducers: {
    clearTourEvents: (state) => {
      state.tourEvents = [];
    },
    clearTourTravels: (state) => {
      state.tourTravels = [];
    },
    setCurrentSequenceId: (state, action: PayloadAction<number | null>) => {
      state.currentSequenceId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // getTourSequences
      .addCase(getTourSequences.fulfilled, (state, action) => {
        state.tourSequences = action.payload;
      })
      // getTourEvents
      .addCase(getTourEvents.pending, (state) => {
        state.isFetchingTourEvents = true;
      })
      .addCase(getTourEvents.fulfilled, (state, action) => {
        state.isFetchingTourEvents = false;
        state.tourEvents = action.payload;
      })
      .addCase(getTourEvents.rejected, (state) => {
        state.isFetchingTourEvents = false;
      })
      // getTourTravels
      .addCase(getTourTravels.pending, (state) => {
        state.isFetchingTourTravels = true;
      })
      .addCase(getTourTravels.fulfilled, (state, action) => {
        state.isFetchingTourTravels = false;
        state.tourTravels = action.payload;
      })
      .addCase(getTourTravels.rejected, (state) => {
        state.isFetchingTourTravels = false;
      })
      //
      .addCase(prepareAndAddTourTravel.pending, (state) => {
        state.isCreatingTourTravel = true;
      })
      .addCase(prepareAndAddTourTravel.fulfilled, (state) => {
        state.isCreatingTourTravel = false;
      })
      .addCase(prepareAndAddTourTravel.rejected, (state) => {
        state.isCreatingTourTravel = false;
      })
      // isImportingTourEvents
      .addCase(bulkAddTourEvent.pending, (state) => {
        state.isCreatingTourEvent = true;
      })
      .addCase(bulkAddTourEvent.fulfilled, (state) => {
        state.isCreatingTourEvent = false;
      })
      .addCase(bulkAddTourEvent.rejected, (state) => {
        state.isCreatingTourEvent = false;
      })
      // isImportingTourEvents
      .addCase(importTourEventsFromOrfeo.pending, (state) => {
        state.isImportingTourEvents = true;
      })
      .addCase(importTourEventsFromOrfeo.fulfilled, (state) => {
        state.isImportingTourEvents = false;
      })
      .addCase(importTourEventsFromOrfeo.rejected, (state) => {
        state.isImportingTourEvents = false;
      })
      // isImportingTourEventsFromCsv
      .addCase(importTourEventsFromCsv.pending, (state) => {
        state.isImportingTourEventsFromCsv = true;
      })
      .addCase(importTourEventsFromCsv.fulfilled, (state) => {
        state.isImportingTourEventsFromCsv = false;
      })
      .addCase(importTourEventsFromCsv.rejected, (state) => {
        state.isImportingTourEventsFromCsv = false;
      });
  },
});
